import { useCallback, useEffect, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import TransactionOverlay from '../transactions/TransactionOverlay';
import { getJournalEntryById } from '../../utils/journalEntryApi';
import { formatCurrency } from '../../utils/formatCurrency';
import { getReportSourceLabel } from '../../utils/reportSourceNavigation';
import type { JournalEntry } from '../../types/journalEntry';

export interface JournalEntryPreviewModalProps {
  journalEntryId: number;
  onClose: () => void;
}

const formatJournalReference = (entry: JournalEntry) => {
  if (!entry.source) return entry.memo ?? '—';
  const label = getReportSourceLabel(entry.source);
  return `${entry.source.number} (${label})`;
};

const JournalEntryPreviewModal = ({ journalEntryId, onClose }: JournalEntryPreviewModalProps) => {
  const [entry, setEntry] = useState<JournalEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadEntry = useCallback(async () => {
    if (Number.isNaN(journalEntryId)) {
      setError('Invalid journal entry ID');
      setLoading(false);
      return;
    }

try {
      setLoading(true);
      setError(null);
      setEntry(await getJournalEntryById(journalEntryId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load journal entry');
      setEntry(null);
    } finally {
      setLoading(false);
    }
  }, [journalEntryId]);

  useEffect(() => {
    void loadEntry();
  }, [loadEntry]);

  return (
    <TransactionOverlay
      variant="preview"
      title={entry?.je_number ?? 'Journal Entry'}
      subtitle={entry ? `Journal Entry · ${entry.txn_date}` : 'Loading…'}
      onClose={onClose}
    >
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-800" />
        </div>
      ) : error ? (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      ) : entry ? (
        <div className="space-y-5">
          <div className="flex flex-wrap gap-2">
            {entry.is_auto_generated && (
              <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                System generated
              </span>
            )}
            {entry.totals.is_balanced ? (
              <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Balanced
              </span>
            ) : (
              <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                Out of balance
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 gap-3 text-sm bg-gray-50 rounded-lg p-4">
            <div>
              <span className="text-gray-500 block text-xs">Date</span>
              <span>{entry.txn_date}</span>
            </div>
            <div>
              <span className="text-gray-500 block text-xs">Reference</span>
              <span>{formatJournalReference(entry)}</span>
            </div>
            {entry.memo && (
              <div>
                <span className="text-gray-500 block text-xs">Memo</span>
                <span>{entry.memo}</span>
              </div>
            )}
            {entry.created_by && (
              <div>
                <span className="text-gray-500 block text-xs">Created by</span>
                <span>{entry.created_by.name}</span>
              </div>
            )}
          </div>

          {entry.lines.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px] text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Account</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Description</th>
                    <th className="px-3 py-2 text-right font-medium text-gray-600">Debit</th>
                    <th className="px-3 py-2 text-right font-medium text-gray-600">Credit</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {entry.lines.map((line) => (
                    <tr key={line.line_number}>
                      <td className="px-3 py-2 text-gray-900">
                        <span className="font-mono">{line.account_number}</span>
                        <span className="mx-1 text-gray-300">—</span>
                        <span>{line.account_name}</span>
                      </td>
                      <td className="px-3 py-2 text-gray-600">{line.description ?? '—'}</td>
                      <td className="px-3 py-2 text-right font-mono text-gray-900">
                        {line.posting_type === 'DEBIT' ? formatCurrency(line.amount) : '—'}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-gray-900">
                        {line.posting_type === 'CREDIT' ? formatCurrency(line.amount) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50 border-t-2 border-gray-300 font-semibold">
                    <td colSpan={2} className="px-3 py-2 text-right text-gray-900">
                      Totals
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-gray-900">
                      {formatCurrency(entry.totals.debit)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-gray-900">
                      {formatCurrency(entry.totals.credit)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          <div className="flex justify-end pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm"
            >
              Close
            </button>
          </div>
        </div>
      ) : null}
    </TransactionOverlay>
  );
};

export default JournalEntryPreviewModal;
